import { isEqual } from 'lodash';
import { isTypingUpdate, markNonUserUpdate, markTypingUpdate, mergeDerivedGradings } from './Components/specDataUtils';

/**
 * Fields shared by the two spec save paths — the auto-save and the explicit Save button.
 * Both post the same endpoint with a hand-built object, so anything they compute
 * differently lets one path quietly undo the other's work.
 */

/**
 * Chooses the custom size-range names to persist. The live edited map wins; an emptied
 * map is a deliberate choice and is kept rather than falling back. Never returns
 * undefined, because the server coerces a missing value to an empty map.
 * @param {object|null|undefined} editedNames names currently held in component state
 * @param {object|null|undefined} loadedNames names the document was loaded with
 * @returns {object}
 */
export const resolveCustomSizeRangeNames = (editedNames, loadedNames) => editedNames ?? loadedNames ?? {};

/**
 * Answers whether a row is one the user has just added and has never been written to the
 * server. Added rows are given a `temp_` id, which the server replaces on the save that
 * first stores them.
 * @param {{id?: string}} [specPomLibrary] one row
 * @returns {boolean}
 */
export const isNewSpecRow = (specPomLibrary) => String(specPomLibrary?.id || '').startsWith('temp_');

/**
 * Gives every row a client-side identity that survives an edit to its code.
 *
 * The server sends POM rows with no id of their own — the code is the only identifier that makes
 * the round trip — so the table used to key its rows by code. Editing a code then changed the key,
 * and React answered by throwing the row away and mounting a new one, which destroyed the input
 * the user was typing into along with their cursor (BM-9510).
 *
 * A row that already carries an identity keeps it, so ids stay put across reloads of the same
 * rows. Rows are matched to their previous selves by code, which is what the server load has to
 * work with; anything unmatched is new to the screen and gets a fresh id.
 * @param {Array<object>} [specPomLibraries] rows arriving on screen
 * @param {Array<object>} [previousRows] rows already on screen, to carry identities over from
 * @returns {Array<object>} rows that all carry a `rowId`, reusing the input row objects when they already did
 */
export const withStableSpecRowIds = (specPomLibraries, previousRows) => {
  if (!Array.isArray(specPomLibraries)) {
    return specPomLibraries;
  }
  if (specPomLibraries.every((specPomLibrary) => specPomLibrary?.rowId)) {
    return specPomLibraries;
  }
  const previousByCode = new Map(
    (Array.isArray(previousRows) ? previousRows : []).filter((row) => row?.code && row?.rowId).map((row) => [row.code, row.rowId]),
  );
  let nextId = 0;
  return specPomLibraries.map((specPomLibrary) => {
    if (specPomLibrary?.rowId) {
      return specPomLibrary;
    }
    nextId += 1;
    const carriedId = specPomLibrary?.code ? previousByCode.get(specPomLibrary.code) : null;
    return { ...specPomLibrary, rowId: carriedId || `row_${Date.now()}_${nextId}` };
  });
};

/**
 * Narrows the rows on screen to the ones the autosave may carry: every row the server already
 * holds, plus each POM the user has added once it has a code and a description.
 *
 * A POM still missing either field stays out, and so does one whose code another row already uses,
 * because the server rejects both and a rejected write would take the rest of the spec down with
 * it. This list is never written back into component state, so a row left out keeps whatever the
 * user is part-way through typing (BM-9510). Before BM-10142 an added POM was left out until the
 * Save button stored it, so its details were lost whenever the user left without pressing Save.
 * @param {Array<object>} [specPomLibraries] rows currently on screen
 * @returns {Array<object>} a new array holding the rows the autosave may write
 */
export const getAutosavableSpecRows = (specPomLibraries) => {
  if (!Array.isArray(specPomLibraries)) {
    return [];
  }
  const codeCounts = specPomLibraries.reduce((counts, specPomLibrary) => {
    const code = trimmedText(specPomLibrary?.code);
    return code ? counts.set(code, (counts.get(code) || 0) + 1) : counts;
  }, new Map());
  return specPomLibraries.filter(
    (specPomLibrary) => !isNewSpecRow(specPomLibrary) || (isCompleteSpecRow(specPomLibrary) && codeCounts.get(trimmedText(specPomLibrary.code)) === 1),
  );
};

/**
 * Answers whether the rows an autosave would carry actually differ from the ones it last saw.
 *
 * Editing a row replaces that row and leaves every sibling's identity alone, so comparing the
 * elements tells a real edit apart from a re-render — and, more to the point, tells a change
 * confined to a newly added row apart from one the server needs to hear about.
 * @param {Array<object>|null} [previousRows] rows as of the last check
 * @param {Array<object>} [nextRows] rows now
 * @returns {boolean}
 */
export const haveSpecRowsChanged = (previousRows, nextRows) => {
  if (previousRows === nextRows) {
    return false;
  }
  if (!Array.isArray(previousRows) || !Array.isArray(nextRows)) {
    return true;
  }
  if (previousRows.length !== nextRows.length) {
    return true;
  }
  return nextRows.some((row, index) => row !== previousRows[index]);
};


/**
 * Numbers the rows a save is about to write.
 *
 * `sequence` is what the server sorts `specPomLibraries` by, and a save fills only a sequence a
 * row does not yet have. Reordering a POM is the one action that may renumber rows that already
 * have a number, so `hasReorderedRows` has to be true for this to touch them: an ordinary save —
 * an edited measurement, a hidden size, an added POM — leaves every existing number alone and
 * cannot move a row the user never dragged.
 *
 * When it does renumber, the numbers are not handed out from the row positions. The screen holds
 * one page of POMs at a time, and renumbering from 1 would speak for rows it never loaded. The
 * rows give back the numbers they already hold, sorted, and take them again in the order they are
 * now shown in: the set of numbers in the document is unchanged, so no row outside the page can
 * move. A row carrying no number has never been ordered against anything and is numbered after the
 * page's highest, which is where the server already sorts it.
 * @param {Array<{sequence?: number}>} [specPomLibraries] rows in the order the save will write them
 * @param {object} [options]
 * @param {boolean} [options.hasReorderedRows] whether the user dragged a POM into a new position
 * @returns {Array<number>} one sequence per row, aligned by position with the input
 */
export const buildSpecRowSequences = (specPomLibraries, { hasReorderedRows = false } = {}) => {
  const rows = Array.isArray(specPomLibraries) ? specPomLibraries : [];
  if (!hasReorderedRows) {
    return rows.map((row, index) => row?.sequence ?? index + 1);
  }
  const slots = rows
    .map((row) => Number(row?.sequence))
    .filter((slot) => Number.isFinite(slot) && slot > 0)
    .sort((first, second) => first - second);
  const highestSlot = slots.length > 0 ? slots[slots.length - 1] : 0;
  return rows.map((row, index) => (index < slots.length ? slots[index] : highestSlot + 1 + index - slots.length));
};

/**
 * Rebuilds the full list of spec POM codes. Only one page of POMs is loaded at a time, so
 * the codes of unloaded rows have to be carried through the save or the server drops them.
 *
 * A code leaves the list when the user deleted it, or when they renamed a row away from it.
 * Subtracting the loaded page instead — on the assumption every loaded row comes back in
 * `specPomLibraries` — makes the list depend on the rows being on screen at save time, and a save
 * fired while they are empty or still hydrating then reports the whole loaded page as removed. The
 * server honours that list, so the rows are erased rather than restored (BM-9510).
 *
 * Renames have to be named explicitly for the same reason: a POM is identified by its code, so
 * editing one reads as a brand new POM while the old code sits untouched in the loaded list. The
 * server then keeps both and the rename appears as a second row (BM-9510).
 * @param {object} params
 * @param {Array<{code?: string}>} [params.specPomLibraries] rows currently on screen
 * @param {Array<string>} [params.deletedSpecPomCodes] codes the user deleted this session
 * @param {Array<string>} [params.renamedSpecPomCodes] codes a saved row was renamed away from this session
 * @param {object} [params.productGrading] the document as it was loaded
 * @returns {Array<string>}
 */
export const buildSpecPomAllCodes = ({ specPomLibraries, deletedSpecPomCodes, renamedSpecPomCodes, productGrading } = {}) => {
  const rows = Array.isArray(specPomLibraries) ? specPomLibraries : [];
  const deletedSet = new Set(Array.isArray(deletedSpecPomCodes) ? deletedSpecPomCodes : []);
  const renamedSet = new Set(Array.isArray(renamedSpecPomCodes) ? renamedSpecPomCodes : []);
  const originalAllCodes = productGrading?.specPomAllCodes || [];

  const incomingCodes = rows.map((pom) => pom?.code).filter(Boolean);
  const survivingCodes = originalAllCodes.filter((code) => Boolean(code) && !deletedSet.has(code) && !renamedSet.has(code));

  return [...new Set([...incomingCodes, ...survivingCodes])];
};

/**
 * Answers whether a save is about to post an empty specification over rows that are still
 * there. The screen holds one page of POMs, and several paths clear that page without the
 * user deleting anything — leaving the route, a grading group tab switch, a reload that has
 * not resolved yet. Each of those marks the rows dirty, because an empty array is truthy,
 * so the autosave that follows carries nothing at all. Writing it is never what the user
 * asked for: a real deletion arrives with the deleted codes recorded alongside it.
 * @param {object} params
 * @param {Array} [params.specPomLibraries] rows the save is about to write
 * @param {Array} [params.loadedSpecPomLibraries] rows the document was loaded with
 * @param {Array<string>} [params.deletedSpecPomCodes] codes the user deleted this session
 * @returns {boolean} True when the save would erase rows nobody deleted.
 */
export const isDestructiveEmptySpecSave = ({ specPomLibraries, loadedSpecPomLibraries, deletedSpecPomCodes } = {}) => {
  const outgoingCount = Array.isArray(specPomLibraries) ? specPomLibraries.length : 0;
  const loadedCount = Array.isArray(loadedSpecPomLibraries) ? loadedSpecPomLibraries.length : 0;
  const deletedCount = Array.isArray(deletedSpecPomCodes) ? deletedSpecPomCodes.length : 0;

  return outgoingCount === 0 && loadedCount > 0 && deletedCount === 0;
};

/**
 * Answers whether the spec-save effect may stop without queueing anything.
 *
 * Unchanged saved rows are the signal that a change was confined to a POM the user is still
 * adding, which stays theirs until they press Save. They are NOT a signal that nothing happened:
 * hiding a size leaves every grading intact and reuses each row object so the memoised cells
 * survive, so the rows compare equal while the document genuinely changed and has to reach the
 * server. A caller that asked for the save, or a deletion, therefore always wins over
 * the row comparison — both are completed actions the rows cannot describe.
 * @param {object} params
 * @param {boolean} params.savedRowsChanged Whether the already-saved rows differ from last time
 * @param {boolean} params.wasExplicitlyRequested Whether a caller asked for this save
 * @param {boolean} params.wasDeletion Whether a POM deletion triggered this save
 * @returns {boolean} True when the effect may return without saving.
 */
export const shouldSkipSpecSave = ({ savedRowsChanged, wasExplicitlyRequested, wasDeletion } = {}) =>
  !savedRowsChanged && !wasExplicitlyRequested && !wasDeletion;

/**
 * Reads a code or description as the server will judge it.
 * @param {*} value
 * @returns {string}
 */
const trimmedText = (value) => (typeof value === 'string' ? value.trim() : '');

/**
 * Answers whether a row carries the two fields the server requires of every POM.
 * @param {{code?: string, description?: string}} [specPomLibrary] one row
 * @returns {boolean}
 */
export const isCompleteSpecRow = (specPomLibrary) => Boolean(trimmedText(specPomLibrary?.code)) && Boolean(trimmedText(specPomLibrary?.description));

/**
 * The key a row is told apart by on the client: its `rowId`, or the `temp_` id it was created with.
 * @param {{rowId?: string, id?: string}} [specPomLibrary] one row
 * @returns {string|null}
 */
export const specRowKey = (specPomLibrary) => specPomLibrary?.rowId || specPomLibrary?.id || null;

/**
 * Drops the client-side `temp_` id from a row, leaving every other field.
 *
 * The `temp_` id only means "never stored". Posting it let the server store it, and from then on
 * the saved POM still read as a new one: autosave skipped it, the save response overwrote what the
 * user had typed, and every load pushed it below the saved rows (BM-10142).
 * @param {object} specPomLibrary one row
 * @returns {object} the same row when it has no `temp_` id, otherwise a copy without it
 */
export const withoutTempId = (specPomLibrary) => {
  if (!isNewSpecRow(specPomLibrary)) {
    return specPomLibrary;
  }
  const { id, ...persistedRow } = specPomLibrary;
  return persistedRow;
};

/**
 * Marks the new rows a successful save carried as saved.
 *
 * The rows are matched by their client key, not by code, so a row the user renamed while the save
 * was in flight is still found. The code it was stored under is reported back so the next save can
 * retire it, the same way a rename of any other saved row is retired. The key is kept as the row's
 * `rowId` so the table does not remount the row the user may still be typing in.
 * @param {Array<object>} specPomLibraries rows currently on screen
 * @param {Array<{key: string, code: string}>} sentNewRows the new rows the save carried
 * @returns {{rows: Array<object>, renamedCodes: Array<string>}} rows, the same array when nothing changed
 */
export const promoteSavedNewRows = (specPomLibraries, sentNewRows) => {
  const sentCodeByKey = new Map((Array.isArray(sentNewRows) ? sentNewRows : []).map((sentRow) => [sentRow.key, sentRow.code]));
  if (!Array.isArray(specPomLibraries) || sentCodeByKey.size === 0) {
    return { rows: specPomLibraries, renamedCodes: [] };
  }
  const renamedCodes = [];
  let didChange = false;
  const rows = specPomLibraries.map((specPomLibrary) => {
    const key = specRowKey(specPomLibrary);
    if (!isNewSpecRow(specPomLibrary) || !sentCodeByKey.has(key)) {
      return specPomLibrary;
    }
    const sentCode = sentCodeByKey.get(key);
    if (sentCode && sentCode !== specPomLibrary.code) {
      renamedCodes.push(sentCode);
    }
    didChange = true;
    return withoutTempId({ ...specPomLibrary, rowId: key });
  });
  return { rows: didChange ? rows : specPomLibraries, renamedCodes };
};

/**
 * Keeps the server's re-derived gradings only for the rows a save carried.
 *
 * The server answers with every row it holds. A row the save left out — one still being typed in —
 * has nothing new to learn from the server, and merging the server's older copy over it threw away
 * what the user had just entered (BM-10142).
 * @param {Array<{code?: string}>} [derivedRows] rows the server re-derived
 * @param {Array<{code?: string}>} [sentRows] rows the save posted
 * @returns {Array<object>}
 */
export const pickDerivedForSentRows = (derivedRows, sentRows) => {
  const sentCodes = new Set((Array.isArray(sentRows) ? sentRows : []).map((sentRow) => sentRow?.code).filter(Boolean));
  return Array.isArray(derivedRows) ? derivedRows.filter((derivedRow) => sentCodes.has(derivedRow?.code)) : [];
};

/**
 * Finds the copy of a row as it was loaded, which the amber highlight compares against.
 *
 * Matched by the row's client key first and its code second, never by position: dragging a POM or
 * deleting one moves rows without moving their loaded copies, and a positional lookup then
 * compared a row with its neighbour (BM-10142).
 * @param {Array<object>} [originalRows] rows as loaded
 * @param {object} [specPomLibrary] the row on screen
 * @returns {object|null}
 */
export const findOriginalSpecRow = (originalRows, specPomLibrary) => {
  if (!Array.isArray(originalRows) || !specPomLibrary) {
    return null;
  }
  const key = specRowKey(specPomLibrary);
  const byKey = key ? originalRows.find((originalRow) => specRowKey(originalRow) === key) : null;
  if (byKey) {
    return byKey;
  }
  return specPomLibrary.code ? originalRows.find((originalRow) => originalRow?.code === specPomLibrary.code) || null : null;
};

/**
 * Places the rows the server does not hold yet back where the user had them in a reload.
 *
 * Each pending row follows the nearest row above it that the reload still holds, or leads the list
 * when nothing above it does. Appending them all at the bottom undid a drag of a new POM on every
 * reload (BM-10142).
 * @param {Array<object>} serverRows rows the server returned, in its order
 * @param {Array<object>} previousRows rows on screen before the reload
 * @param {Function} isPending answers whether a previous row is one the server does not hold
 * @returns {Array<object>} the server rows when nothing is pending
 */
export const mergePendingSpecRows = (serverRows, previousRows, isPending) => {
  const rows = Array.isArray(serverRows) ? serverRows : [];
  const pendingRows = (Array.isArray(previousRows) ? previousRows : []).filter(isPending);
  if (pendingRows.length === 0) {
    return serverRows;
  }
  const serverCodes = new Set(rows.map((row) => row?.code).filter(Boolean));
  const pendingByAnchor = new Map();
  let anchorCode = null;
  previousRows.forEach((previousRow) => {
    if (isPending(previousRow)) {
      pendingByAnchor.set(anchorCode, [...(pendingByAnchor.get(anchorCode) || []), previousRow]);
    } else if (previousRow?.code && serverCodes.has(previousRow.code)) {
      anchorCode = previousRow.code;
    }
  });
  return [...(pendingByAnchor.get(null) || []), ...rows.flatMap((row) => [row, ...((row?.code && pendingByAnchor.get(row.code)) || [])])];
};

/**
 * Answers whether a change to the loaded grading document only synced its code list.
 *
 * A save reports the codes it persisted back into the store, which hands out a new document object
 * that still holds the rows as they were first loaded. Re-seeding the screen from those rows put
 * back values the user had changed and the server had already saved (BM-10142). A real load brings
 * fresh row objects, so a sync is told apart by its rows being the loaded ones or a non-empty subset
 * of them, with every other field of the document unchanged.
 * @param {object|null} [previousGrading] the document the rows were last seeded from
 * @param {object|null} [nextGrading] the document now in the store
 * @returns {boolean}
 */
export const isSpecCodeListOnlyUpdate = (previousGrading, nextGrading) => {
  if (!previousGrading || !nextGrading || previousGrading === nextGrading) {
    return false;
  }
  if (previousGrading._id !== nextGrading._id || String(previousGrading.gradingGroupId) !== String(nextGrading.gradingGroupId)) {
    return false;
  }
  const previousRows = previousGrading.specPomLibraries;
  const nextRows = nextGrading.specPomLibraries;
  if (!Array.isArray(previousRows) || !Array.isArray(nextRows)) {
    return false;
  }
  if (nextRows !== previousRows && (nextRows.length === 0 || !nextRows.every((row) => previousRows.includes(row)))) {
    return false;
  }
  const fieldNames = new Set([...Object.keys(previousGrading), ...Object.keys(nextGrading)]);
  return [...fieldNames].every((fieldName) => CODE_LIST_SYNC_FIELDS.includes(fieldName) || previousGrading[fieldName] === nextGrading[fieldName]);
};

/** The only fields a code-list sync changes on the loaded grading document. */
const CODE_LIST_SYNC_FIELDS = ['specPomAllCodes', 'specPomLibraries'];

/**
 * Builds the rows a save posts, as copies.
 *
 * Both save paths used to fill blank measurements, tolerances and the sequence by writing straight
 * into the row objects held in component state, which changed rows the memoised table had already
 * rendered without it knowing (BM-10142). The `temp_` id and the `rowId` render key are dropped
 * here so the server stores neither; both only mean something on this screen.
 * @param {Array<object>} [specPomLibraries] rows to post, in the order they will be written
 * @param {Function} resolveSequence (row, index) => the sequence the row is posted with
 * @returns {Array<object>} new row objects; the input rows are untouched
 */
export const buildSpecSavePayloadRows = (specPomLibraries, resolveSequence) =>
  (Array.isArray(specPomLibraries) ? specPomLibraries : []).map((specPomLibrary, index) => {
    const { rowId, ...row } = withoutTempId(specPomLibrary);
    const sequence = resolveSequence(row, index);
    if (!row?.gradings?.length) {
      return { ...row, sequence };
    }
    return {
      ...row,
      gradings: row.gradings.map((grading) => (grading?.measurment ? grading : { ...grading, measurment: 0 })),
      tolMinus: row.tolMinus || 0,
      tolPlus: row.tolPlus || 0,
      sequence,
    };
  });

/**
 * Applies a successful save to the spec state.
 *
 * The new rows the save stored become saved rows, and the server's re-derived gradings are merged
 * into every row except one the save did not carry. Only the deleted and renamed codes this save
 * carried are cleared, because a code recorded while the save was in flight still has to reach the
 * server with the next one. A new row renamed during the save has its stored code queued for
 * retirement.
 *
 * The settled rows are tagged as an app update only when nothing changed on screen since the save
 * was sent; otherwise they still carry a user edit the next save has to pick up, so a keystroke
 * keeps its typing tag and a completed edit stays untagged.
 * @param {object} state component state holding SPECData, deletedSpecPomCodes and renamedSpecPomCodes
 * @param {object} [result]
 * @param {Array<{key: string, code: string}>} [result.sentNewRows] new rows the save carried
 * @param {Array<string>} [result.sentDeletedCodes] deleted codes the save carried
 * @param {Array<string>} [result.sentRenamedCodes] renamed-away codes the save carried
 * @param {Array<object>} [result.derivedSpecGradings] derived gradings to merge, already limited to the sent rows
 * @param {boolean} [result.rowsUnchangedSinceSave] whether the rows on screen are still the ones the save sent
 * @returns {object} the same state when nothing changed
 */
export const settleSpecSave = (state, { sentNewRows = [], sentDeletedCodes = [], sentRenamedCodes = [], derivedSpecGradings = [], rowsUnchangedSinceSave = true } = {}) => {
  const { rows: promotedRows, renamedCodes } = promoteSavedNewRows(state.SPECData, sentNewRows);
  const settledRows = derivedSpecGradings.length > 0 ? keepUnsentNewRows(mergeDerivedGradings(promotedRows, derivedSpecGradings), promotedRows) : promotedRows;
  const previousDeleted = state.deletedSpecPomCodes || [];
  const previousRenamed = state.renamedSpecPomCodes || [];
  const deletedSpecPomCodes = withoutSentCodes(previousDeleted, sentDeletedCodes);
  const keptRenamed = withoutSentCodes(previousRenamed, sentRenamedCodes);
  const renamedSpecPomCodes = [...keptRenamed, ...renamedCodes.filter((code) => !keptRenamed.includes(code))];
  const rowsChanged = settledRows !== state.SPECData;
  const listsChanged = deletedSpecPomCodes.length !== previousDeleted.length || !isEqualList(renamedSpecPomCodes, previousRenamed);
  if (!rowsChanged && !listsChanged) {
    return state;
  }
  return {
    ...state,
    ...(rowsChanged && { SPECData: carrySpecUpdateMarker(settledRows, state.SPECData, rowsUnchangedSinceSave) }),
    deletedSpecPomCodes: listsChanged ? deletedSpecPomCodes : previousDeleted,
    renamedSpecPomCodes: listsChanged ? renamedSpecPomCodes : previousRenamed,
  };
};

/**
 * Compares two code lists element by element.
 * @param {Array<string>} first
 * @param {Array<string>} second
 * @returns {boolean}
 */
const isEqualList = (first, second) => first.length === second.length && first.every((code, index) => code === second[index]);

/**
 * Points a row's images back at their stored keys for the Save button's write, as a copy.
 *
 * The screen shows signed URLs; the document stores keys. A freshly uploaded image is still a
 * `data:image/` string and is left for the server to upload. The `fromLib` marker is screen-only.
 * This used to be done by writing into the row objects held in component state (BM-10142).
 * @param {object} specPomLibrary one payload row
 * @returns {object} a new row
 */
export const toStoredImageFields = (specPomLibrary) => {
  const { fromLib, ...row } = specPomLibrary || {};
  const storedKey = (key, value) => (row[key] && !String(row[value] || '').startsWith('data:image/') ? { [value]: row[key] } : {});
  return {
    ...row,
    ...storedKey('imageKey', 'image'),
    ...storedKey('fullImageKey', 'fullImage'),
    ...storedKey('annotatedImageKey', 'annotatedImage'),
  };
};

/**
 * Undoes a derived-gradings merge on every row that is still new after the save.
 *
 * The merge matches rows by code, so a new row the save left out — one still being typed in, or one
 * whose code duplicates a saved row — picked up the saved row's gradings and lost what the user had
 * typed (BM-10142). `mergeDerivedGradings` keeps row positions, so the rows line up by index.
 * @param {Array<object>} mergedRows rows after the merge
 * @param {Array<object>} rowsBeforeMerge the same rows before it
 * @returns {Array<object>} rowsBeforeMerge when no saved row changed
 */
const keepUnsentNewRows = (mergedRows, rowsBeforeMerge) => {
  if (mergedRows === rowsBeforeMerge) {
    return rowsBeforeMerge;
  }
  let didChange = false;
  const rows = mergedRows.map((mergedRow, index) => {
    const rowBeforeMerge = rowsBeforeMerge[index];
    if (isNewSpecRow(rowBeforeMerge)) {
      return rowBeforeMerge;
    }
    didChange = didChange || mergedRow !== rowBeforeMerge;
    return mergedRow;
  });
  return didChange ? rows : rowsBeforeMerge;
};

/**
 * Removes one occurrence of each code a save carried. A code recorded again while the save was in
 * flight is a second occurrence and stays for the next save; filtering by value dropped it too.
 * @param {Array<string>} recordedCodes codes recorded so far
 * @param {Array<string>} sentCodes codes the save carried
 * @returns {Array<string>}
 */
const withoutSentCodes = (recordedCodes, sentCodes) => {
  const remainingSent = [...sentCodes];
  return recordedCodes.filter((code) => {
    const sentIndex = remainingSent.indexOf(code);
    if (sentIndex === -1) {
      return true;
    }
    remainingSent.splice(sentIndex, 1);
    return false;
  });
};

/**
 * Tags settled rows so the save effect reads them correctly: an app update when the rows are still
 * the ones the save sent, a keystroke when the rows it settled carried one, untagged otherwise.
 * @param {Array<object>} settledRows rows after the settle
 * @param {Array<object>} rowsBeforeSettle rows the settle was applied to
 * @param {boolean} rowsUnchangedSinceSave whether the rows on screen are still the ones the save sent
 * @returns {Array<object>} settledRows
 */
const carrySpecUpdateMarker = (settledRows, rowsBeforeSettle, rowsUnchangedSinceSave) => {
  if (rowsUnchangedSinceSave) {
    return markNonUserUpdate(settledRows);
  }
  return isTypingUpdate(rowsBeforeSettle) ? markTypingUpdate(settledRows) : settledRows;
};

/**
 * Drops the `fromLib` lock from every row once the Save button has stored them. The marker locks a
 * POM imported from a library until it is saved; the Save button used to clear it by writing into
 * the row objects held in state (BM-10142).
 * @param {Array<object>} specPomLibraries rows on screen
 * @returns {Array<object>} the same array when no row is locked
 */
export const withoutLibraryMarkers = (specPomLibraries) => {
  if (!Array.isArray(specPomLibraries) || !specPomLibraries.some((specPomLibrary) => specPomLibrary?.fromLib)) {
    return specPomLibraries;
  }
  return specPomLibraries.map((specPomLibrary) => {
    if (!specPomLibrary?.fromLib) {
      return specPomLibrary;
    }
    const { fromLib, ...savedRow } = specPomLibrary;
    return savedRow;
  });
};

/**
 * Puts recalculated rows back in place, in the order the rows are on screen now.
 *
 * A recalculation runs over a snapshot of the rows. Rebuilding the list from that snapshot put
 * every new POM after the saved ones, undoing a drag on every load (BM-10142). Each row is swapped
 * only while it is still the row the recalculation read, so an edit made in the meantime is kept.
 * A new row a save promoted in the meantime is still that row — the same row less its `temp_` id —
 * and takes its recalculation without the id coming back.
 * @param {Array<object>} latestRows rows as they are now
 * @param {Map<object, object>} recalculatedByRow recalculated row, keyed by the row object it was computed from
 * @returns {Array<object>} the same array when nothing was swapped
 */
export const replaceRecalculatedRows = (latestRows, recalculatedByRow) => {
  if (!Array.isArray(latestRows)) {
    return latestRows;
  }
  const promotedSnapshots = new Map(
    [...recalculatedByRow.keys()].filter((snapshotRow) => isNewSpecRow(snapshotRow) && specRowKey(snapshotRow)).map((snapshotRow) => [specRowKey(snapshotRow), snapshotRow]),
  );
  let didChange = false;
  const nextRows = latestRows.map((row) => {
    const recalculatedRow = recalculatedByRow.get(row) || recalculationForPromotedRow(row, promotedSnapshots, recalculatedByRow);
    if (!recalculatedRow || recalculatedRow === row) {
      return row;
    }
    didChange = true;
    return recalculatedRow;
  });
  return didChange ? nextRows : latestRows;
};

/**
 * Finds the recalculation of a row that a save promoted after the recalculation read it.
 * @param {object} row a row as it is now
 * @param {Map<string, object>} promotedSnapshots new-row snapshots by client key
 * @param {Map<object, object>} recalculatedByRow recalculated row by snapshot
 * @returns {object|null} the recalculation without its `temp_` id, or null
 */
const recalculationForPromotedRow = (row, promotedSnapshots, recalculatedByRow) => {
  if (isNewSpecRow(row)) {
    return null;
  }
  const snapshotRow = promotedSnapshots.get(specRowKey(row));
  if (!snapshotRow || !isEqual(row, withoutTempId({ ...snapshotRow, rowId: specRowKey(snapshotRow) }))) {
    return null;
  }
  return withoutTempId({ ...recalculatedByRow.get(snapshotRow), rowId: specRowKey(snapshotRow) });
};

/**
 * Drops the client-only `temp_` id and `rowId` render key from spec rows crossing the server
 * boundary, in either direction.
 *
 * Older saves stored both with each row. A stored `temp_` id made a saved POM read as unsaved, and a
 * stored `rowId` was reused as the row's key, so rows copied from one another shared a key and were
 * matched to the wrong row (BM-10142). Rows read back lose them, and so do rows the screen sends to
 * a library; the screen hands out render keys itself, carried over by code across reloads.
 * @param {Array<object>} [specPomLibraries] rows
 * @returns {Array<object>} the same array when no row carries either
 */
export const withoutClientKeys = (specPomLibraries) => {
  if (!Array.isArray(specPomLibraries) || !specPomLibraries.some((specPomLibrary) => isNewSpecRow(specPomLibrary) || specPomLibrary?.rowId !== undefined)) {
    return specPomLibraries;
  }
  return specPomLibraries.map((specPomLibrary) => {
    const { rowId, ...loadedRow } = withoutTempId(specPomLibrary);
    return loadedRow;
  });
};

/**
 * Lets a reload of the spec keep what the user has typed but not saved yet.
 *
 * A reload — the first "add POM" on a spec still loading page by page fetches every row — used to
 * replace the rows on screen with the server's copy, so an edit still inside the autosave window
 * was lost (BM-10142). Each reloaded row is compared with the copy the screen was last loaded
 * from: while the server still holds exactly that, the row on screen is the newer one and is kept.
 * When the server has changed the row since — a save landed, or the server re-derived it — the
 * server's row wins.
 * @param {Array<object>} serverRows rows just reloaded, in server order
 * @param {Array<object>} [previousServerRows] rows as last loaded from the server
 * @param {Array<object>} [clientRows] rows on screen
 * @returns {Array<object>} one row per server row; the same array when nothing is kept
 */
export const keepUnsavedRowEdits = (serverRows, previousServerRows, clientRows) => {
  if (!Array.isArray(serverRows) || !Array.isArray(previousServerRows) || !Array.isArray(clientRows)) {
    return serverRows;
  }
  const previousByCode = new Map(previousServerRows.filter((row) => row?.code).map((row) => [row.code, row]));
  const clientByCode = new Map(clientRows.filter((row) => row?.code).map((row) => [row.code, row]));
  let didKeep = false;
  const rows = serverRows.map((serverRow) => {
    const clientRow = serverRow?.code ? clientByCode.get(serverRow.code) : null;
    const previousRow = serverRow?.code ? previousByCode.get(serverRow.code) : null;
    if (!clientRow || !previousRow || !isEqual(serverRow, previousRow)) {
      return serverRow;
    }
    didKeep = true;
    return clientRow;
  });
  return didKeep ? rows : serverRows;
};

/**
 * Answers whether a store update carries a page the infinite scroll has just loaded.
 *
 * The store keeps the last appended page on the document, so every later update to that document —
 * the code-list sync after each save among them — still carries it. Treating each of those as a new
 * page re-appended every row of it missing from the screen, which put back a POM the user had just
 * deleted from that page; the next autosave then stored it again (BM-10142). A page is new only the
 * first time it arrives.
 * @param {object|null} [previousGrading] the document the screen last applied
 * @param {object|null} [nextGrading] the document now in the store
 * @returns {boolean}
 */
export const isNewScrollAppend = (previousGrading, nextGrading) =>
  Boolean(nextGrading?.scrollAppendedLibraries) && nextGrading.scrollAppendedLibraries !== previousGrading?.scrollAppendedLibraries;

/**
 * Merges every row of a spec into the rows on screen, for the first "add POM" on a spec still
 * loading page by page.
 *
 * The rows on screen keep the order the user gave them — a drag is only stored by the Save button,
 * so the server's order can be older — and each takes the server's copy only when the server has
 * changed it since it was loaded, so an unsaved edit survives (`keepUnsavedRowEdits`). Rows the
 * screen never loaded are added after them in server order. A loaded row no longer on screen was
 * deleted or renamed away by the user and is not brought back (BM-10142).
 * @param {Array<object>} allServerRows every row of the spec, as the server holds it
 * @param {Array<object>} [loadedServerRows] rows as the screen loaded them
 * @param {Array<object>} [clientRows] rows on screen
 * @returns {Array<object>}
 */
export const mergeAllRowsIntoScreen = (allServerRows, loadedServerRows, clientRows) =>
  orderRowsLikeScreen(keepUnsavedRowEdits(Array.isArray(allServerRows) ? allServerRows : [], loadedServerRows, clientRows), clientRows, loadedServerRows);

/**
 * Lays reloaded rows out in the order they are on screen.
 *
 * Each row on screen is replaced by its reloaded copy, matched by code; a row the reload does not
 * hold — a POM still being added — stays as it is, where it is. Rows the screen never loaded follow
 * in the reload's order. A loaded row no longer on screen was deleted or renamed away and is left
 * out. Used where the screen's order is newer than the server's: a drag is only stored by the Save
 * button (BM-10142).
 * @param {Array<object>} serverRows reloaded rows, already merged with any edits to keep
 * @param {Array<object>} [clientRows] rows on screen
 * @param {Array<object>} [loadedServerRows] rows as the screen loaded them
 * @returns {Array<object>}
 */
export const orderRowsLikeScreen = (serverRows, clientRows, loadedServerRows) => {
  const reloadedRows = Array.isArray(serverRows) ? serverRows : [];
  const rowsOnScreen = Array.isArray(clientRows) ? clientRows : [];
  const reloadedByCode = new Map(reloadedRows.filter((row) => row?.code).map((row) => [row.code, row]));
  const codesOnScreen = new Set(rowsOnScreen.map((row) => row?.code).filter(Boolean));
  const loadedCodes = new Set((Array.isArray(loadedServerRows) ? loadedServerRows : []).map((row) => row?.code).filter(Boolean));
  const orderedOnScreen = rowsOnScreen.map((row) => (row?.code && reloadedByCode.has(row.code) ? reloadedByCode.get(row.code) : row));
  const neverLoaded = reloadedRows.filter((row) => row?.code && !codesOnScreen.has(row.code) && !loadedCodes.has(row.code));
  return [...orderedOnScreen, ...neverLoaded];
};
